/**
 * CompetitionModals - Modal Management Component
 *
 * Single responsibility: Rendering all modals for CompetitionView.
 * Separates modal UI from main component logic.
 */

import React from 'react';
import QuickPicksModal from '@/components/modals/QuickPicksModal';
import PicksUpdatedModal from '@/components/modals/PicksUpdatedModal';
import PickemLeaderboard from '@/components/leaderboard/PickemLeaderboard';

interface CompetitionModalsProps {
  // Modal states
  isQuickPickOpen: boolean;
  isFavPickOpen: boolean;
  isClearOpen: boolean;
  isSuccessOpen: boolean;
  isLeaderboardOpen: boolean;

  // Modal handlers
  onCloseQuickPick: () => void;
  onCloseFavPick: () => void;
  onCloseClear: () => void;
  onCloseLeaderboard: () => void;

  // Action handlers
  onConfirmQuickPick: () => void;
  onConfirmFavPick: () => void;
  onConfirmClear: () => void;
  /** Where any exit from the post-entry popup sends the player, and what its CTA is called. */
  onExitAfterEntry: () => void;
  exitAfterEntryLabel: string;
  /**
   * Contest-uploaded artwork for the post-entry popup (desktop 336x280, optional mobile
   * 279x170). When either is set the popup shows it instead of the GAM slot.
   */
  postEntryImageUrl?: string;
  postEntryImageUrlMobile?: string;

  // Data
  gameId: string;
  currentUserId?: string;
}

/**
 * Component that renders all modals for CompetitionView
 */
export const CompetitionModals: React.FC<CompetitionModalsProps> = ({
  isQuickPickOpen,
  isFavPickOpen,
  isClearOpen,
  isSuccessOpen,
  isLeaderboardOpen,
  onCloseQuickPick,
  onCloseFavPick,
  onCloseClear,
  onCloseLeaderboard,
  onConfirmQuickPick,
  onConfirmFavPick,
  onConfirmClear,
  onExitAfterEntry,
  exitAfterEntryLabel,
  postEntryImageUrl,
  postEntryImageUrlMobile,
  gameId,
  currentUserId,
}) => {
  return (
    <>
      {/* Quick Pick Confirmation Modal */}
      <QuickPicksModal
        isOpen={isQuickPickOpen}
        onClose={onCloseQuickPick}
        onConfirm={onConfirmQuickPick}
        title="Quick Pick"
        description="Randomly select outcomes for your remaining selections?"
        confirmText="Yes, add quick picks"
        type="quick"
      />

      {/* Popular Pick Confirmation Modal */}

      <QuickPicksModal
        isOpen={isFavPickOpen}
        onClose={onCloseFavPick}
        onConfirm={onConfirmFavPick}
        title="Popular Pick"
        description="Select favorite outcomes for your remaining selections?"
        confirmText="Yes, add popular picks"
        type="fav"
      />

      {/* Clear Selections Confirmation Modal */}
      <QuickPicksModal
        isOpen={isClearOpen}
        onClose={onCloseClear}
        onConfirm={onConfirmClear}
        title="Clear Picks"
        description="This action clears all your current picks. Are you sure you would like to clear all your picks?"
        confirmText="Yes, clear my picks"
        type="clear"
      />

      {/* Post-entry confirmation. It carries the POST_ENTRY ad slot, which is the only
          place that placement is served, so it opens on every successful submit — or the
          contest's own uploaded artwork, which takes that space when there is any. Every way
          out of it — CTA, X, Escape, overlay — moves the player on to the same place:
          the lobby app for DK, My Picks for SkyPicks. */}
      <PicksUpdatedModal
        isOpen={isSuccessOpen}
        onExit={onExitAfterEntry}
        exitLabel={exitAfterEntryLabel}
        postEntryImageUrl={postEntryImageUrl}
        postEntryImageUrlMobile={postEntryImageUrlMobile}
      />

      {/* Leaderboard Modal */}
      <PickemLeaderboard
        pickemId={gameId}
        isOpen={isLeaderboardOpen}
        onClose={onCloseLeaderboard}
        currentUserId={currentUserId}
      />
    </>
  );
};
